export enum UserRole {
  Admin = 1,
  User = 2
}

export interface InviteToken {
  token?: string;
  valida?: boolean;
  email?: string;
  idCircuito?: number;
}

export interface Screen {
  idPos?;
  nombre?;
  evento?;
  fecha?;
  version?;
  ip?;
  fecha_relativa?;
  idGrupo?;
  nombreGrupo?;
}

export interface Grupo {
  id?;
  nombre?: String;
}

export interface User {
  token?;
  remenber?;
  name?: string;
  email?: string;
  role?: UserRole;
  activated?: boolean;
  circuito?: any;
  trialEndsAt?: string;
  subscriptionStatus?: 'trial' | 'active' | 'expired';
  guid?: string;
}

export interface ProgramacionBundle {
  frameNoticia?;
  framePos?;
  frameStreming?;
  frameUrl?;
  frameZocalo?;
  framesContenido?;
  framesPublicidad?;
  grupo?;
  spots?;
  zocalo?;
  equipo?;
  frameProgramado?;
}

export interface ProgramacionBundleResponse {
  code?;
  status?;
  data: ProgramacionBundle;
}

export interface Frame {
  id?;
  idTipo?;
  idFrame?;
  idCircuito?;
  idGrupo?;
  idMedia?;
  idPos?;
  nombre?;
  path?;
  posicion?;
  segundos?;
  tipo?;
  general?;
  exclusive?;
  time_actual;
  slot?;
  desde?;
  hasta?;
  desde_fecha?;
  desde_hora?;
  hasta_fecha?;
  hasta_hora?;
  programacion?;
}

export interface PlanFeatureFlags { zocalos?: boolean; multicaja?: boolean; franjasHorarias?: boolean; monitoreo?: boolean; }
export interface Plan {
  id: string; name: string; amount: string; currency: string; billing?: string;
  planId?: string; planName?: string; tier?: string;
  maxScreens?: number | null; screensInUse?: number | null;
  features?: PlanFeatureFlags; billingCycle?: string;
  description?: string | null; popular?: boolean; selfServe?: boolean;
  featuresDisplay?: string[];
}
export interface PayPalConfig { clientId: string; currency: string; }
export interface PaymentProvider { id: string; name: string; clientId?: string; currency?: string; }
export interface SubscriptionInfo {
  subscriptionExpiresAt: string | null;
  billingCycle: 'mensual' | 'anual' | null;
  paymentProvider: string | null;
  daysUntilExpiry: number | null;
  accountStatus: 'TRIAL' | 'ACTIVE' | 'EXPIRED';
  trialEndsAt?: string | null;
  paymentReference?: string | null;
}
export interface TrialStatusResponse {
  accountStatus: 'TRIAL' | 'ACTIVE' | 'EXPIRED';
  subscriptionStatus?: 'trial' | 'active' | 'expired';
  trialEndsAt?: string | null;
  subscriptionExpiresAt?: string | null;
  billingCycle?: 'mensual' | 'anual' | null;
  paymentProvider?: string | null;
  daysUntilExpiry?: number | null;
  planId?: string;
  planName?: string;
  tier?: string;
  maxScreens?: number | null;
  screensInUse?: number | null;
  features?: PlanFeatureFlags;
  // Real C3 nests the plan under `entitlement` and sends no feature flags.
  // The mapper reads it as fallback (top-level first) so gates and counters
  // work against the real backend, not just the top-level mocks.
  entitlement?: {
    status?: string;
    plan?: {
      id?: string;
      planId?: string;
      name?: string;
      planName?: string;
      tier?: string;
      billing?: string;
      amount?: string | null;
      currency?: string;
      maxScreens?: number | null;
    } | null;
    unlimited?: boolean;
  } | null;
}
// Alias for compat with previous naming
export interface AccountStatusResponse extends TrialStatusResponse {}
// Payload-driven entitlement snapshot (contratos-congelados Slice A).
// All fields nullable: an old-backend status omits every additive field and
// maps to a null-filled Entitlement, which downstream gates treat as fail-open.
export interface Entitlement {
  planId: string | null;
  planName: string | null;
  tier: string | null;
  maxScreens: number | null;
  screensInUse: number | null;
  features: PlanFeatureFlags | null;
  billingCycle: string | null;
}
// Pure mapper: never throws, null-fills every absent field (explicit
// null checks preserve meaningful falsy values such as screensInUse 0).
// Precedence per field: top-level C3 fields first, nested entitlement.plan
// second, null last. Flags: explicit flags first, canonical-tier fallback
// (TIER_FLAGS) second, null (fail-open) when the tier is unknown.
import { TIER_FLAGS } from './plan-display';
export function toEntitlement(status?: TrialStatusResponse | null): Entitlement {
  var nested: any = status && (status as any).entitlement && (status as any).entitlement.plan
    ? (status as any).entitlement.plan : null;
  var rawTier: string = status && (status as any).tier != null ? String((status as any).tier)
    : (nested && nested.tier != null ? String(nested.tier) : null);
  var tierKey: string = rawTier ? rawTier.toLowerCase() : null;
  var features: PlanFeatureFlags | null = status && (status as any).features != null
    ? (status as any).features : null;
  if (!features && tierKey && TIER_FLAGS[tierKey]) {
    features = {
      multicaja: TIER_FLAGS[tierKey].multicaja,
      franjasHorarias: TIER_FLAGS[tierKey].franjasHorarias,
      zocalos: TIER_FLAGS[tierKey].zocalos,
      monitoreo: TIER_FLAGS[tierKey].monitoreo
    };
  }
  return {
    planId: status && (status as any).planId != null ? (status as any).planId
      : (nested && nested.planId != null ? nested.planId
        : (nested && nested.id != null ? nested.id : null)),
    planName: status && (status as any).planName != null ? (status as any).planName
      : (nested && nested.planName != null ? nested.planName
        : (nested && nested.name != null ? nested.name : null)),
    tier: status && (status as any).tier != null ? (status as any).tier
      : (nested && nested.tier != null ? nested.tier : null),
    maxScreens: status && (status as any).maxScreens != null ? (status as any).maxScreens
      : (nested && nested.maxScreens != null ? nested.maxScreens : null),
    screensInUse: status && (status as any).screensInUse != null ? (status as any).screensInUse : null,
    features: features,
    billingCycle: status && (status as any).billingCycle != null ? (status as any).billingCycle : null
  };
}
export interface PlansResponse { plans: Plan[]; providers?: PaymentProvider[]; paypal?: PayPalConfig; }

export interface MaintainerCircuit {
  guid: string;
  name: string;
  userEmail: string;
  status: 'TRIAL' | 'ACTIVE' | 'EXPIRED' | 'LIFETIME';
  billingCycle: 'mensual' | 'anual' | null;
  paymentProvider: string | null;
  paymentReferenceMasked: string | null;
  lastPaymentAt: string | null;
  subscriptionExpiresAt: string | null;
  daysUntilExpiry: number | null;
  circuitoId: number;
}

export interface MaintainerUser {
  id: number;
  email: string;
  name: string;
  lockout: boolean;
  circuitoCount: number;
  role: string;
}

export interface PaymentHistoryEntry {
  id: number;
  circuito_id: number;
  payment_provider: string;
  payment_reference: string;
  plan_id: string;
  amount: number | null;
  currency: string | null;
  billing_cycle: string | null;
  created_at: string;
}

export interface MaintainerPaginated<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  circuits?: T[];
  users?: T[];
  payments?: T[];
  items?: T[];
}



// WEBPACK FOOTER //
// ./src/app/providers/models.ts